import {
  CircleAlertIcon,
  GitPullRequestIcon,
  TicketIcon,
  TriangleAlertIcon,
  type LucideIcon,
} from "lucide-react"
import { useId, type ReactNode } from "react"
import { Badge } from "@/primitives/badge.tsx"
import { Skeleton } from "@/primitives/skeleton.tsx"
import { cn } from "@/primitives/utils"
import { Callout } from "./callout.tsx"
import { GoogleMark } from "./marks.tsx"
import { StatusDot } from "./status-dot.tsx"

/*
 * One account Beacon reaches, as a card: a Member's own on Connections, or Beacon's own
 * connection to a tracker on Trackers or to GitHub on GitHub. It says whose it is, whether it
 * works, what Beacon may do with it, and the one thing to do about it.
 */

/**
 * A tile drawn in muted ink from what Beacon reaches there, since Beacon draws no tracker's or code
 * host's brand: a ticket for a tracker, a pull request for GitHub.
 */
const mutedMark =
  (Icon: LucideIcon) =>
  ({ size }: { size: 16 | 20 }) => (
    <Icon
      aria-hidden
      className={cn("shrink-0 text-muted-foreground", size === 16 ? "size-4" : "size-5")}
    />
  )

/** Each account's mark, drawn in its own tile. */
const marks = {
  google: GoogleMark,
  jira: mutedMark(TicketIcon),
  github: mutedMark(GitPullRequestIcon),
} as const

/**
 * The connection's state as a dot and a word. A failed connection is a fault to notice, so it is
 * `feed-error`, never a warning. `unreachable` is a connection whose other end didn't answer as
 * expected, which may pass by itself; `failed` is one refused, which won't. `expiring` works now
 * but its credential ends soon, which is something to act on before it stops: a warning.
 */
const statuses = {
  connected: { tone: "success", label: "Connected" },
  expiring: { tone: "warning", label: "Expiring soon" },
  failed: { tone: "feed-error", label: "Needs reconnecting" },
  unreachable: { tone: "feed-error", label: "Not answering" },
  none: { tone: "idle", label: "Not connected" },
} as const

/**
 * `status` is where the connection stands: `none` until the Member connects. `meta` is the page's
 * sentence about it ("Connected since 22 September 2026"), and `scope` what Beacon may read
 * ("Read-only · Drive"). `promise`, what connecting hands over, is shown only while nothing is
 * connected. `action` is the card's one control, Connect or Reconnect, or nothing when there is
 * nothing to do here; `more` is its RowActions, holding Disconnect, after the action on the desk.
 * `failure`, the sentence for why a failed or unreachable connection stopped, is set in a
 * `feed-error` Callout across the card's foot.
 * `warning`, the sentence for what to act on before a connection stops, such as a credential
 * expiring, is set in a `warning` Callout there instead, on any connection that hasn't failed.
 * On `compact` the action wraps under the text, in line with it, and `more` stands at the card's
 * top right, beside the title, so a ⋯ never has a line to itself; only the title gives way to it.
 */
export function IntegrationCard({
  mark,
  title,
  status,
  meta,
  scope,
  promise,
  action,
  more,
  failure,
  warning,
}: {
  mark: keyof typeof marks
  title: string
  status: keyof typeof statuses
  meta?: string
  scope: string
  promise?: string
  action: ReactNode
  more?: ReactNode
  failure?: string
  warning?: string
}) {
  const heading = useId()
  const Mark = marks[mark]
  const { tone, label } = statuses[status]
  return (
    <section
      aria-labelledby={heading}
      className="space-y-4 rounded-lg bg-card p-5 shadow-raised ring-1 ring-border compact:relative"
    >
      <div className="flex gap-4 compact:flex-wrap">
        <span
          aria-hidden
          className="grid size-10 shrink-0 place-items-center rounded-lg bg-surface-sunken ring-1 ring-border-subtle"
        >
          <Mark size={20} />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          {/* On `compact` the ⋯ stands over this line's end, so the title leaves it room. */}
          <h2 id={heading} className={cn("text-title font-semibold", more && "compact:pr-8")}>
            {title}
          </h2>
          <StatusDot tone={tone} label={label} />
          {meta && <p className="text-xs text-muted-foreground">{meta}</p>}
          <div className="pt-1">
            <Badge variant="neutral">{scope}</Badge>
          </div>
          {status === "none" && promise && (
            <p className="max-w-prose pt-1 text-ui text-muted-foreground">{promise}</p>
          )}
        </div>
        {/* On `compact` this box gives way, so its action and `more` place themselves in the card:
            `more` at its top right, over the title's line alone, so the lines under it keep their
            width. */}
        <div className="flex shrink-0 items-start compact:contents">
          <div className="flex items-center gap-2 compact:contents">
            <div
              className={cn(
                "flex shrink-0 items-start compact:order-last compact:basis-full compact:pl-14",
                !action && "compact:hidden",
              )}
            >
              {action}
            </div>
            {more && (
              <div className="flex shrink-0 items-start compact:absolute compact:top-4 compact:right-4">
                {more}
              </div>
            )}
          </div>
        </div>
      </div>
      {(status === "failed" || status === "unreachable") && failure ? (
        <Callout tone="feed-error" icon={CircleAlertIcon}>
          {failure}
        </Callout>
      ) : (
        status !== "none" &&
        warning && (
          <Callout tone="warning" icon={TriangleAlertIcon}>
            {warning}
          </Callout>
        )
      )}
    </section>
  )
}

/**
 * Where a connection stands, as its dot and word alone, away from its card: Home's Google Drive
 * card, which leads to Connections, says it the same way the card there does.
 */
export function ConnectionStatus({ status }: { status: keyof typeof statuses }) {
  const { tone, label } = statuses[status]
  return <StatusDot tone={tone} label={label} />
}

/**
 * An IntegrationCard while the Member's connections load: the same card, its mark, lines and button
 * in grey at their real places, so the page doesn't move as it arrives: on `compact`, the button's
 * under the text, as the card's action is.
 */
export function IntegrationCardSkeleton() {
  return (
    <div
      role="status"
      aria-busy
      aria-label="Loading"
      className="flex gap-4 rounded-lg bg-card p-5 shadow-raised ring-1 ring-border compact:flex-wrap"
    >
      <Skeleton className="size-10 shrink-0 rounded-lg" />
      <div className="flex-1 space-y-2 pt-0.5">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-52" />
        <Skeleton className="h-5 w-28" />
      </div>
      <div className="shrink-0 compact:basis-full compact:pl-14">
        <Skeleton className="h-7 w-32" />
      </div>
    </div>
  )
}
